import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { courseAPI } from '../services/api';
import { formatCourseDurationLabel } from '../constants/courseDurations';

function courseLearningStatusFromProgress(progress) {
  const st = String(progress?.enrollment_status ?? progress?.enrollmentStatus ?? '').toLowerCase();
  if (st === 'completed') {
    return {
      label: 'Course completed',
      className: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-600/20',
    };
  }
  if (st === 'approved' || st === 'active') {
    return {
      label: 'In progress',
      className: 'bg-amber-100 text-amber-900 ring-1 ring-amber-600/25',
    };
  }
  return null;
}

/** Split day markdown into H2 topic cards; optional intro before first ##. */
function splitMarkdownIntoH2Topics(markdown) {
  const text = (markdown || '').replace(/\r\n/g, '\n').trim();
  if (!text) return [{ title: 'Content', body: '' }];

  const lines = text.split('\n');
  const h2Starts = [];
  for (let i = 0; i < lines.length; i++) {
    if (/^##\s+/.test(lines[i])) {
      const title = lines[i].replace(/^##\s+/, '').trim();
      h2Starts.push({ lineIndex: i, title });
    }
  }

  if (h2Starts.length === 0) {
    return [{ title: 'Topics & examples', body: text }];
  }

  const sections = [];
  const firstIdx = h2Starts[0].lineIndex;
  const intro = lines.slice(0, firstIdx).join('\n').trim();
  if (intro) {
    sections.push({ title: 'Overview', body: intro });
  }

  for (let i = 0; i < h2Starts.length; i++) {
    const start = h2Starts[i].lineIndex + 1;
    const end = i + 1 < h2Starts.length ? h2Starts[i + 1].lineIndex : lines.length;
    const body = lines.slice(start, end).join('\n').trim();
    sections.push({ title: h2Starts[i].title, body });
  }

  return sections;
}

/** Within one H2 body, split on ### for sub-topic panels. */
function splitBodyIntoH3Blocks(body) {
  const text = (body || '').replace(/\r\n/g, '\n').trim();
  if (!text) return [{ subtitle: null, body: '' }];

  const parts = text.split(/(?=^###\s+)/m).filter((p) => p.trim());
  const blocks = [];
  for (const part of parts) {
    const m = part.match(/^###\s+(.+)$/m);
    if (m) {
      const subtitle = m[1].trim();
      const rest = part.replace(/^###\s+.+\n?/m, '').trim();
      blocks.push({ subtitle, body: rest });
    } else {
      blocks.push({ subtitle: null, body: part.trim() });
    }
  }
  return blocks.length ? blocks : [{ subtitle: null, body: text }];
}

const markdownComponents = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-semibold text-gray-900 mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-gray-800 mt-3 mb-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-gray-800 mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-gray-700 leading-relaxed my-2 text-sm">{children}</p>,
  ul: ({ children }) => <ul className="my-2 list-disc pl-5 text-sm text-gray-700 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 list-decimal pl-5 text-sm text-gray-700 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-primary-200 bg-primary-50/50 pl-3 py-1 my-3 text-sm text-gray-700 italic">
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a href={href} className="text-primary-600 underline hover:text-primary-800" target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full text-left text-xs text-gray-700">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border-b border-gray-200 bg-gray-50 px-2 py-2 font-semibold">{children}</th>,
  td: ({ children }) => <td className="border-b border-gray-100 px-2 py-2">{children}</td>,
  hr: () => <hr className="my-6 border-gray-200" />,
  pre: ({ children }) => (
    <pre className="my-3 overflow-x-auto rounded-lg border border-gray-800 bg-gray-900 p-4 text-xs text-gray-100">
      {children}
    </pre>
  ),
  code: ({ inline, className, children, ...props }) => {
    if (inline) {
      return (
        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[0.85em] text-gray-900" {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className={`block font-mono whitespace-pre ${className || ''}`} {...props}>
        {children}
      </code>
    );
  },
};

function MarkdownBlock({ markdown }) {
  return (
    <div className="course-md prose-flow max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

const CandidateCoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDayNumber, setActiveDayNumber] = useState(1);
  const [courseProgress, setCourseProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [submissionUploading, setSubmissionUploading] = useState(false);
  const [submissionFile, setSubmissionFile] = useState(null);

  const sortedDays = useMemo(() => {
    const days = course?.days || [];
    return [...days].sort((a, b) => Number(a.day_number) - Number(b.day_number));
  }, [course]);

  const completedDays = useMemo(() => {
    const raw = courseProgress?.completed_days ?? courseProgress?.completedDays;
    if (!Array.isArray(raw)) return new Set();
    return new Set(raw.map(Number).filter((n) => n > 0));
  }, [courseProgress]);

  const fetchCourseProgress = useCallback(async () => {
    if (!courseId || user?.userType !== 'candidate') return;
    try {
      setProgressLoading(true);
      const pr = await courseAPI.getCourseEnrollmentProgress(courseId);
      setCourseProgress(pr.data);
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Could not load course progress');
      setCourseProgress(null);
    } finally {
      setProgressLoading(false);
    }
  }, [courseId, user?.userType]);

  useEffect(() => {
    if (user?.userType && user.userType !== 'candidate') {
      toast.error('Only candidates can open this page');
      navigate('/dashboard', { replace: true });
    }
  }, [user?.userType, navigate]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!courseId || user?.userType !== 'candidate') return;
      setLoading(true);
      setError(null);
      try {
        const res = await courseAPI.getCourseContent(courseId);
        if (cancelled) return;
        setCourse(res.data);
        const days = (res.data?.days || []).sort((a, b) => Number(a.day_number) - Number(b.day_number));
        const first = days[0]?.day_number ?? 1;
        setActiveDayNumber(Number(first));
        if (!cancelled) {
          await fetchCourseProgress();
        }
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        const msg = e.response?.data?.detail || 'Could not load course';
        setError(typeof msg === 'string' ? msg : 'Could not load course');
        setCourse(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [courseId, user?.userType, fetchCourseProgress]);

  const isDayUnlocked = useCallback(
    (dayNum) => {
      const n = Number(dayNum);
      if (Number.isNaN(n) || n <= 1) return true;
      return completedDays.has(n - 1);
    },
    [completedDays]
  );

  const activeDay = useMemo(
    () => sortedDays.find((d) => Number(d.day_number) === Number(activeDayNumber)),
    [sortedDays, activeDayNumber]
  );

  const activeUnlocked = activeDay ? isDayUnlocked(activeDay.day_number) : false;

  const h2Topics = useMemo(() => {
    if (!activeDay?.content_md) return [];
    return splitMarkdownIntoH2Topics(activeDay.content_md);
  }, [activeDay]);

  const markDayComplete = async () => {
    if (!user?.id || !courseId || !activeDay || !activeUnlocked) return;
    const n = Number(activeDay.day_number);
    if (Number.isNaN(n) || completedDays.has(n)) return;
    try {
      const res = await courseAPI.markCourseDayComplete(courseId, n);
      setCourseProgress(res.data);
      toast.success(`Day ${n} marked complete`);
      const nextDayNum = n + 1;
      if (sortedDays.some((d) => Number(d.day_number) === nextDayNum)) {
        setActiveDayNumber(nextDayNum);
      }
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Could not save day progress');
    }
  };

  const handleSubmissionUpload = async () => {
    if (!courseId || !submissionFile) {
      toast.error('Choose a file first');
      return;
    }
    const allDone =
      courseProgress?.all_days_complete ?? courseProgress?.allDaysComplete ?? false;
    const canUpload =
      courseProgress?.can_upload_submission ?? courseProgress?.canUploadSubmission ?? false;
    if (!allDone || !canUpload) {
      toast.error('Finish every day before uploading');
      return;
    }
    try {
      setSubmissionUploading(true);
      await courseAPI.uploadCourseSubmission(courseId, submissionFile);
      toast.success('Submission uploaded');
      setSubmissionFile(null);
      await fetchCourseProgress();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setSubmissionUploading(false);
    }
  };

  const handleDownloadSubmission = async () => {
    if (!courseId) return;
    try {
      const res = await courseAPI.downloadCourseSubmission(courseId);
      const name =
        courseProgress?.submission_file_name ||
        courseProgress?.submissionFileName ||
        'course-submission';
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error('Could not download your submission');
    }
  };

  const goBackToCourses = () => {
    navigate('/dashboard', { state: { candidateView: 'courses' } });
  };

  const selectDay = (dayNum) => {
    const n = Number(dayNum);
    if (!isDayUnlocked(n)) {
      toast.error(`Complete day ${n - 1} before opening day ${n}.`);
      return;
    }
    setActiveDayNumber(n);
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-gray-600">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
        <p className="text-sm">Loading course…</p>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-800">{error || 'Course not found.'}</p>
        <button
          type="button"
          onClick={goBackToCourses}
          className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Back to courses
        </button>
      </div>
    );
  }

  const dayComplete = activeDay ? completedDays.has(Number(activeDay.day_number)) : false;

  const allDaysComplete =
    courseProgress?.all_days_complete ?? courseProgress?.allDaysComplete ?? false;
  const canUploadSubmission =
    courseProgress?.can_upload_submission ?? courseProgress?.canUploadSubmission ?? false;
  const hasSubmission =
    courseProgress?.has_submission ?? courseProgress?.hasSubmission ?? false;

  const learningStatus = courseLearningStatusFromProgress(courseProgress);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 2xl:max-w-[90rem]">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button
            type="button"
            onClick={goBackToCourses}
            className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline"
          >
            ← Back to courses
          </button>
          <h1 className="mt-2 flex flex-wrap items-center gap-2 text-2xl font-bold text-gray-900">
            <span>{course.title}</span>
            {learningStatus && (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${learningStatus.className}`}
              >
                {learningStatus.label}
              </span>
            )}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {course.skill_name}
            {course.category ? ` · ${course.category}` : ''} · {formatCourseDurationLabel(course.duration_days)}
          </p>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-gray-700">{course.summary}</p>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <aside className="w-full shrink-0 lg:w-56">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Days</h2>
          <nav className="flex flex-row gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible">
            {sortedDays.map((d) => {
              const n = Number(d.day_number);
              const unlocked = isDayUnlocked(n);
              const done = completedDays.has(n);
              const active = Number(activeDayNumber) === n;
              return (
                <button
                  key={d.id || n}
                  type="button"
                  disabled={!unlocked}
                  onClick={() => selectDay(n)}
                  className={`flex min-w-[8.5rem] flex-col rounded-lg border px-3 py-2 text-left text-sm transition lg:min-w-0 ${
                    active
                      ? 'border-primary-500 bg-primary-50 text-primary-900'
                      : unlocked
                        ? 'border-gray-200 bg-white text-gray-800 hover:border-primary-300 hover:bg-primary-50/40'
                        : 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400'
                  }`}
                >
                  <span className="font-semibold">
                    Day {n}
                    {!unlocked && ' (locked)'}
                  </span>
                  <span className="mt-0.5 line-clamp-2 text-xs font-normal text-gray-500">{d.title}</span>
                  {done && <span className="mt-1 text-xs font-medium text-emerald-600">Completed</span>}
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          {progressLoading && (
            <p className="mb-3 text-xs text-gray-500">Updating your progress from the server…</p>
          )}

          {!activeDay && <p className="text-sm text-gray-600">Select a day to begin.</p>}

          {activeDay && !activeUnlocked && (
            <p className="text-sm text-amber-800">
              This day is locked. Mark the previous day complete to continue.
            </p>
          )}

          {activeDay && activeUnlocked && (
            <>
              <div className="border-b border-gray-100 pb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Day {activeDay.day_number}: {activeDay.title}
                </h2>
                {activeDay.estimated_minutes != null && (
                  <p className="mt-1 text-xs text-gray-500">About {activeDay.estimated_minutes} min</p>
                )}
              </div>

              <div className="mt-6 space-y-8">
                {h2Topics.map((topic, idx) => {
                  const h3Blocks = splitBodyIntoH3Blocks(topic.body);
                  return (
                    <article key={`${topic.title}-${idx}`} className="rounded-lg border border-gray-100 bg-gray-50/40 p-4">
                      <h3 className="text-base font-bold text-gray-900">{topic.title}</h3>
                      <div className="mt-4 space-y-6">
                        {h3Blocks.map((block, j) => (
                          <div
                            key={`${topic.title}-${idx}-h3-${j}`}
                            className="rounded-md border border-gray-200 bg-white p-4 shadow-sm"
                          >
                            {block.subtitle && (
                              <h4 className="mb-3 border-b border-gray-100 pb-2 text-sm font-semibold text-primary-800">
                                {block.subtitle}
                              </h4>
                            )}
                            <MarkdownBlock markdown={block.body} />
                          </div>
                        ))}
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="mt-10 flex flex-col gap-3 border-t border-gray-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-gray-500">
                  Complete this day when you have finished the material. The next day unlocks after you mark this one
                  complete.
                </p>
                <button
                  type="button"
                  disabled={dayComplete || progressLoading}
                  onClick={markDayComplete}
                  className="shrink-0 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {dayComplete ? 'Day completed' : `Mark day ${activeDay.day_number} complete`}
                </button>
              </div>
            </>
          )}

          {courseProgress && (allDaysComplete || hasSubmission) && (
            <div className="mt-8 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
              <h3 className="text-sm font-semibold text-emerald-900">Course submission</h3>
              <p className="mt-1 text-xs text-emerald-900/90">
                When you have finished every day, upload assignments you tried, sample projects you built, or other
                evidence of your work. Supported files include PDF, ZIP, common Office formats, images, and archives—one
                upload per course.
              </p>
              {hasSubmission ? (
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-800">
                    <span className="font-medium">Submitted:</span>{' '}
                    {courseProgress?.submission_file_name || courseProgress?.submissionFileName || 'file'}
                  </p>
                  <button
                    type="button"
                    onClick={handleDownloadSubmission}
                    className="rounded-md border border-emerald-600 bg-white px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-50"
                  >
                    Download my file
                  </button>
                </div>
              ) : canUploadSubmission ? (
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-medium text-gray-700">Choose file</label>
                    <input
                      type="file"
                      onChange={(e) => setSubmissionFile(e.target.files?.[0] || null)}
                      className="block w-full text-sm text-gray-700 file:mr-3 file:rounded file:border-0 file:bg-primary-600 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-primary-700"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={submissionUploading || !submissionFile}
                    onClick={handleSubmissionUpload}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {submissionUploading ? 'Uploading…' : 'Upload submission'}
                  </button>
                </div>
              ) : (
                <p className="mt-2 text-xs text-amber-800">
                  All days are marked complete, but uploads are not available for this enrollment status. Contact
                  support if this looks wrong.
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CandidateCoursePage;
